import React, { useRef, useState } from 'react';
import * as css from './panelHeader.module.scss';
import classNames from 'classnames';
import Icon, { IconType } from '../icon';

export enum HeaderLevel {
  p,
  h2,
  h3,
}

interface Props {
  title: string;
  headerLevel?: HeaderLevel;
  subtitle?: string;
  color?: string;
  backgroundColor?: string;
  className?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  padLeft?: boolean;
  childrenClassName?: string;
  customStatus?: CustomStatus;
}

interface MainHeaderProps extends Props {
  subtitleRef?: React.RefObject<HTMLParagraphElement>;
}

export type CustomStatus = {
  statusTitle: string;
  className: string;
  tooltip?: string;
};

const Line: React.FC<
  React.PropsWithChildren<{
    collapsible?: boolean;
    collapsed?: boolean;
    customStatus?: CustomStatus;
  }>
> = ({ collapsible, collapsed, customStatus }) => {
  return (
    <div className={css.line}>
      {collapsible && (
        <Icon
          type={
            collapsed ? IconType.OpenRowTriangle : IconType.CloseRowTriangle
          }
          className={css.arrow}
        />
      )}
      {customStatus && (
        <h3
          className={classNames(css.customStatus, customStatus.className)}
          title={customStatus.tooltip}
        >
          {customStatus.statusTitle}
        </h3>
      )}
    </div>
  );
};

const Header: React.FC<
  React.PropsWithChildren<{
    title: string;
    headerLevel?: HeaderLevel;
    color?: string;
    backgroundColor?: string;
  }>
> = ({ title, headerLevel = HeaderLevel.h2, color, backgroundColor }) => {
  switch (headerLevel) {
    case HeaderLevel.p:
      return (
        <p>
          <strong
            style={{ color: color, backgroundColor: backgroundColor }}
            className={css.title}
          >
            {title}
          </strong>
        </p>
      );
    case HeaderLevel.h2:
      return (
        <h2
          style={{ color: color, backgroundColor: backgroundColor }}
          className={css.title}
        >
          {title}
        </h2>
      );
    case HeaderLevel.h3:
      return (
        <h3
          style={{ color: color, backgroundColor: backgroundColor }}
          className={css.title}
        >
          {title}
        </h3>
      );
  }
};

const MainHeader: React.FC<React.PropsWithChildren<MainHeaderProps>> = ({
  title,
  headerLevel,
  color,
  backgroundColor,
  subtitle,
  collapsible,
  collapsed,
  subtitleRef,
  customStatus,
}) => (
  <>
    <Header
      title={title}
      headerLevel={headerLevel}
      color={color}
      backgroundColor={backgroundColor}
    />
    <Line
      collapsed={collapsed}
      collapsible={collapsible}
      customStatus={customStatus}
    />
    {subtitle && (
      <p
        className={classNames(css.subtitle, collapsed && css.hide)}
        ref={subtitleRef}
      >
        {subtitle}
      </p>
    )}
  </>
);

const CollapsibleHeader: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  childrenClassName,
  ...props
}) => {
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const [collapsed, setCollapsed] = useState<boolean>(props.collapsed ?? false);
  const [hovered, setHovered] = useState<boolean>(false);

  const onKeyEvent = (event: React.KeyboardEvent) =>
    setCollapsed(['Enter', ' '].includes(event.key) ? !collapsed : collapsed);

  const onMouseUp = (event: React.MouseEvent) =>
    setCollapsed(
      subtitleRef && event.target === subtitleRef.current
        ? collapsed
        : !collapsed,
    );

  const onMouseOver = (event: React.MouseEvent) =>
    setHovered(subtitleRef && event.target !== subtitleRef.current);

  return (
    <section>
      <div
        className={classNames(
          css.header,
          css.collapsible,
          hovered && css.hovered,
          className,
        )}
        tabIndex={0}
        onMouseUp={onMouseUp}
        onKeyDown={onKeyEvent}
        onMouseOver={onMouseOver}
        role={'button'}
        aria-expanded={!collapsed}
      >
        <MainHeader
          collapsed={collapsed}
          subtitleRef={subtitleRef}
          {...props}
        />
      </div>
      <div
        className={classNames(
          css.children,
          collapsed && css.hide,
          childrenClassName,
        )}
      >
        {children}
      </div>
    </section>
  );
};

const PanelHeader: React.FC<React.PropsWithChildren<Props>> = ({
  collapsible,
  padLeft,
  ...props
}) =>
  collapsible ? (
    <CollapsibleHeader {...props} collapsible />
  ) : (
    <div
      className={classNames(
        css.header,
        props.className,
        padLeft && css.padLeft,
      )}
    >
      <MainHeader {...props} />
    </div>
  );

export default PanelHeader;
